import { FileText, Import, LogOut, Settings, User, Users } from "lucide-react";
import { CanAccess, useGetIdentity, useLogout } from "ra-core";
import { Link } from "react-router";

import { useTheme } from "@/components/admin/use-theme";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { ChangelogPage } from "../../atomic-crm/misc/ChangelogPage";
import { ImportPage } from "../../atomic-crm/misc/ImportPage";
import { useWorkspaces } from "../workspace/useWorkspaces";

/**
 * Sidebar footer, reference style: a quiet appearance-toggle row above the
 * current user's identity (avatar, name, role) with an inline sign-out.
 * Uses the real logged-in identity and workspace membership role.
 */
export const SidebarUserFooter = () => {
  const { data: identity } = useGetIdentity();
  const { activeWorkspace } = useWorkspaces();
  const logout = useLogout();
  const { theme, setTheme } = useTheme();
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const initials = (identity?.fullName ?? "?")
    .split(" ")
    .map((part: string) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-8 items-center justify-between rounded-md px-2 group-data-[collapsible=icon]:hidden">
        <span className="text-[12px] text-sidebar-foreground/70">
          {isDark ? "Dark mode" : "Light mode"}
        </span>
        <Switch
          checked={isDark}
          onCheckedChange={(checked) => setTheme(checked ? "dark" : "light")}
          aria-label="Toggle dark mode"
          className="scale-90"
        />
      </div>

      <div className="flex items-center gap-2 rounded-md px-1 py-1">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-md p-1 text-left hover:bg-sidebar-accent"
            >
              <Avatar className="size-7 rounded-full">
                <AvatarImage src={identity?.avatar} alt="" />
                <AvatarFallback className="rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate text-[13px] font-medium text-sidebar-accent-foreground">
                  {identity?.fullName}
                </span>
                <span className="truncate text-[11px] text-sidebar-foreground/60">
                  {activeWorkspace?.roleName ?? ""}
                </span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-52 rounded-lg"
          >
            <DropdownMenuItem asChild>
              <Link to="/profile" className="flex items-center gap-2">
                <User className="size-4" /> My profile
              </Link>
            </DropdownMenuItem>
            <CanAccess resource="sales" action="list">
              <DropdownMenuItem asChild>
                <Link to="/sales" className="flex items-center gap-2">
                  <Users className="size-4" /> Team
                </Link>
              </DropdownMenuItem>
            </CanAccess>
            <CanAccess resource="configuration" action="edit">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <Settings className="size-4" /> Settings
                </Link>
              </DropdownMenuItem>
            </CanAccess>
            <DropdownMenuItem asChild>
              <Link to={ImportPage.path} className="flex items-center gap-2">
                <Import className="size-4" /> Import data
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={ChangelogPage.path} className="flex items-center gap-2">
                <FileText className="size-4" /> What's new
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => logout()}
          aria-label="Sign out"
          title="Sign out"
          className="size-7 shrink-0 rounded-md text-sidebar-foreground/60 hover:text-foreground group-data-[collapsible=icon]:hidden"
        >
          <LogOut className="size-3.5" />
        </Button>
      </div>
    </div>
  );
};
